import {Doctor, DoctorOptions} from "../dashboard";
import {
    getDoctorTitle,
    getPatientTitle,
    DoctorTitleCase,
    PatientTitleCase
} from "../../utils/common";
import {ImportAliasMap, ImportEntityType, ImportMapping, ImportTargetField, ImportUploadResponse} from "../../services/impl/ImportDataProvider";

export const firstLetterToUpperCase = (value: string): string =>
    value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "";

export type MappingTarget = ImportTargetField;

export type ImportUploadResponseWithMapping = ImportUploadResponse & {
    mapping?: ImportMapping;
};

export const ENTITY_OPTIONS: {text: string; value: ImportEntityType}[] = [
    {text: firstLetterToUpperCase(getPatientTitle(PatientTitleCase.tab)), value: "patient"},
    {text: firstLetterToUpperCase(getDoctorTitle(DoctorTitleCase.tab)), value: "doctor"},
];

export const ENTITY_NAME_A = {
    patient: getPatientTitle(PatientTitleCase.a),
    doctor: getDoctorTitle(DoctorTitleCase.a)
};

export const PATIENT_TITLE_OV = getPatientTitle(PatientTitleCase.ov);
export const DOCTOR_TITLE_IM = firstLetterToUpperCase(getDoctorTitle(DoctorTitleCase.im));
export const DOCTOR_TITLE_A = firstLetterToUpperCase(getDoctorTitle(DoctorTitleCase.a));
export const DOCTOR_TITLE_A_LOWER = getDoctorTitle(DoctorTitleCase.a);

export const getRequiredTargets = (targetFields: MappingTarget[]): string[] =>
    targetFields
        .filter((field) => field.required)
        .map((field) => field.value);

export const getTargetOptions = (targetFields: MappingTarget[]): MappingTarget[] =>
    targetFields;

const TARGET_LABEL_OBSOLETE_UID_SUFFIX = " (обязательный внешний идентификатор)";

const getTargetLabel = (targetOptions: MappingTarget[], value: string): string => {
    const found = targetOptions.find((item) => item.value === value);
    if (found) {
        return found.text.replace(TARGET_LABEL_OBSOLETE_UID_SUFFIX, "");
    }
    return value;
};

export const getMappingError = (mapping: ImportMapping, requiredTargets: string[], targetOptions: MappingTarget[]): string => {
    const mappedTargets = Object.values(mapping).filter((value) => !!value);
    const missing = requiredTargets.filter((target) => !mappedTargets.includes(target));
    if (missing.length > 0) {
        const names = missing.map((target) => getTargetLabel(targetOptions, target));
        if (names.length === 1) {
            return `Обязательное поле "${names[0]}" должно быть сопоставлено`;
        }
        return `Обязательные поля "${names.join(", ")}" должны быть сопоставлены`;
    }

    const duplicates = mappedTargets.filter((value, index) => mappedTargets.indexOf(value) !== index);
    if (duplicates.length > 0) {
        const duplicateTargetLabel = getTargetLabel(targetOptions, duplicates[0]);
        return `Поле "${duplicateTargetLabel}" сопоставлено более чем к одной колонке`;
    }

    return "";
};

const EMPTY_MAPPING_ALIASES: ImportAliasMap = {};

const isDateLikeHeader = (value: string): boolean => {
    const normalized = normalizeValue(value);
    return DATE_HEADER_HINTS.some((hint) => normalized.includes(hint));
};

export const DATE_TARGET_FIELDS: Record<ImportEntityType, string[]> = {
    patient: ["dateofbirth"],
    doctor: []
};
const DATE_HEADER_HINTS = ["дата", "time", "date", "birth", "dob", "рожд", "датарождения", "срок"];

export const isDateMappedField = (entityType: ImportEntityType, targetField?: string): boolean => {
    if (!targetField) {
        return false;
    }
    return DATE_TARGET_FIELDS[entityType].includes(targetField);
};

const excelSerialToDateString = (rawValue: string): string => {
    if (!rawValue) {
        return "";
    }
    const normalized = rawValue.replace(",", ".");
    const serial = Number(normalized);
    if (Number.isNaN(serial) || !Number.isFinite(serial) || serial <= 1 || serial >= 1000000) {
        return "";
    }

    const excelEpoch = new Date(Date.UTC(1899, 11, 30, 0, 0, 0));
    const date = new Date(excelEpoch.getTime() + Math.floor(serial) * 86400000);
    if (isNaN(date.getTime())) {
        return "";
    }
    return date.toLocaleDateString("ru-RU");
};

export const MAX_PREVIEW_ROWS = 20;
export const EMPTY_COLUMN_OPTION: MappingTarget = {text: "— Не загружать —", value: "", required: undefined};
export const formatPreviewValue = (entityType: ImportEntityType, sourceColumn: string, rawValue: string, mappedField?: string): string => {
    const value = rawValue?.trim() ?? "";
    if (!value) {
        return "";
    }

    const shouldFormatDate = isDateMappedField(entityType, mappedField) || isDateLikeHeader(sourceColumn);
    if (shouldFormatDate) {
        const fromSerial = excelSerialToDateString(value);
        if (fromSerial) {
            return fromSerial;
        }
        const parsedDate = new Date(value);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString("ru-RU");
        }
    }

    return value;
};

export const formatBytes = (value: number): string => {
    if (value === 0) return "0 Б";
    const units = ["Б", "КБ", "МБ", "ГБ"];
    const idx = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
    const num = value / Math.pow(1024, idx);
    return `${num.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
};

export const formatDateTime = (value?: string | Date): string => {
    if (!value) {
        return "";
    }
    const date = typeof value === "string" ? new Date(value) : value;
    if (isNaN(date.getTime())) {
        return "";
    }
    return date.toLocaleString("ru-RU");
};

export const buildDoctorLabel = (doctor: Doctor): string => {
    return `${doctor.surname ?? ""} ${doctor.name ?? ""} ${doctor.middlename ?? ""}`.trim().replace(/\s+/g, " ");
};

export const normalizeValue = (value: string): string => {
    return value
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[^\wа-яё]/g, "")
        .replace(/ё/g, "е");
};

export const getFieldBySource = (
    _entityType: ImportEntityType,
    sourceField: string,
    mapped: ImportMapping,
    aliases: ImportAliasMap = EMPTY_MAPPING_ALIASES
): string => {
    const current = mapped[sourceField];
    if (current) {
        return current;
    }

    const normalized = normalizeValue(sourceField);
    const aliasesByTarget = aliases ?? EMPTY_MAPPING_ALIASES;
    const candidates: string[] = [];

    for (const [target, aliasList] of Object.entries(aliasesByTarget)) {
        if (aliasList.map(normalizeValue).includes(normalized)) {
            if (!candidates.includes(target)) {
                candidates.push(target);
            }
        }
    }

    return candidates[0] ?? "";
};

export const buildInitialMapping = (
    session: Pick<ImportUploadResponse, "detectedColumns">,
    entityType: ImportEntityType,
    targetFields: MappingTarget[],
    savedMapping?: ImportMapping,
    aliases?: ImportAliasMap
): ImportMapping => {
    const result: ImportMapping = {};
    const available = getTargetOptions(targetFields).map((field) => field.value);
    const usedTargets = new Set<string>();

    session.detectedColumns.forEach((sourceColumn) => {
        const predefined = savedMapping && savedMapping[sourceColumn];
        if (predefined && available.includes(predefined)) {
            if (!usedTargets.has(predefined)) {
                result[sourceColumn] = predefined;
                usedTargets.add(predefined);
            }
            return;
        }

        const guessed = getFieldBySource(entityType, sourceColumn, savedMapping ?? {}, aliases);
        if (guessed && available.includes(guessed) && !usedTargets.has(guessed)) {
            result[sourceColumn] = guessed;
            usedTargets.add(guessed);
        }
    });

    return result;
};

export const buildMappingFromConfig = (
    session: Pick<ImportUploadResponse, "detectedColumns">,
    entityType: ImportEntityType,
    targetFields: MappingTarget[],
    savedMapping: ImportMapping,
    aliases?: ImportAliasMap
): ImportMapping => {
    const result: ImportMapping = {};
    const available = getTargetOptions(targetFields).map((field) => field.value);
    const usedTargets = new Set<string>();
    const normalizedSavedMapping = Object.entries(savedMapping).reduce<Record<string, string>>((acc, [source, target]) => {
        if (target && available.includes(target)) {
            acc[normalizeValue(source)] = target;
        }
        return acc;
    }, {});
    const savedTargets = new Set(Object.values(savedMapping).filter((target) => available.includes(target)));

    session.detectedColumns.forEach((sourceColumn) => {
        const exactTarget = savedMapping[sourceColumn];
        const normalizedTarget = normalizedSavedMapping[normalizeValue(sourceColumn)];
        const target = exactTarget || normalizedTarget;
        if (target && available.includes(target) && !usedTargets.has(target)) {
            result[sourceColumn] = target;
            usedTargets.add(target);
        }
    });

    session.detectedColumns.forEach((sourceColumn) => {
        if (result[sourceColumn]) {
            return;
        }

        const guessed = getFieldBySource(entityType, sourceColumn, {}, aliases);
        if (guessed && savedTargets.has(guessed) && !usedTargets.has(guessed)) {
            result[sourceColumn] = guessed;
            usedTargets.add(guessed);
        }
    });

    return result;
};

export const mapDoctorOptions = (docs: Doctor[]): DoctorOptions[] =>
    docs
        .filter((doctor: Doctor) => !!doctor?._id)
        .map((doctor: Doctor) => ({
            key: doctor._id,
            value: doctor._id,
            text: buildDoctorLabel(doctor) || doctor.tel || doctor._id,
        }));
